import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Briefcase, Loader2, Calendar as CalIcon } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { GlassCard, PageHeader, EmptyState, StatusBadge } from "../components/shared";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const STAGES = ["applied", "shortlisted", "interview", "assessment", "selected", "rejected"];

export default function Recruitment() {
    const { user } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openJob, setOpenJob] = useState(false);
    const [openInterview, setOpenInterview] = useState(null);
    const canManage = ["super_admin", "hr"].includes(user?.role);

    const load = async () => {
        try {
            const [j, c] = await Promise.all([api.get("/jobs"), api.get("/candidates")]);
            setJobs(j.data.items || []);
            setCandidates(c.data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const moveStage = async (id, stage) => {
        try {
            await api.post(`/candidates/${id}/stage`, { stage });
            toast.success(`Moved to ${stage}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const onDrop = (e, stage) => {
        e.preventDefault();
        const id = e.dataTransfer.getData("candidate-id");
        if (id) moveStage(id, stage);
    };

    return (
        <div>
            <PageHeader
                title="Recruitment"
                subtitle="Hire smarter — manage jobs and candidates in one place."
                testId="recruitment-header"
                actions={
                    canManage && (
                        <Dialog open={openJob} onOpenChange={setOpenJob}>
                            <DialogTrigger asChild>
                                <Button className="rounded-full" data-testid="new-job-btn"><Plus className="w-4 h-4 mr-1" /> New job</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Post a new job</DialogTitle></DialogHeader>
                                <JobForm onCreated={() => { setOpenJob(false); load(); }} />
                            </DialogContent>
                        </Dialog>
                    )
                }
            />

            <Tabs defaultValue="pipeline">
                <TabsList>
                    <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline</TabsTrigger>
                    <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
                </TabsList>

                <TabsContent value="pipeline" className="mt-4">
                    {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : (
                        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide" data-testid="kanban-board">
                            {STAGES.map((stage) => {
                                const stageItems = candidates.filter((c) => c.stage === stage);
                                return (
                                    <div
                                        key={stage}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => onDrop(e, stage)}
                                        className="flex-shrink-0 w-72 bg-black/[0.03] dark:bg-white/[0.03] rounded-2xl p-3 border border-black/5 dark:border-white/10"
                                        data-testid={`column-${stage}`}
                                    >
                                        <div className="flex items-center justify-between mb-3 px-1">
                                            <div className="text-[11px] uppercase tracking-wider font-semibold capitalize">{stage}</div>
                                            <span className="text-xs text-muted-foreground">{stageItems.length}</span>
                                        </div>
                                        <div className="space-y-2">
                                            {stageItems.length === 0 ? (
                                                <div className="text-center text-xs text-muted-foreground py-6">No candidates</div>
                                            ) : (
                                                stageItems.map((c) => (
                                                    <motion.div
                                                        key={c.id}
                                                        layout
                                                        draggable={canManage}
                                                        onDragStart={(e) => e.dataTransfer.setData("candidate-id", c.id)}
                                                        className="bg-card rounded-xl p-3 border border-black/5 dark:border-white/10 cursor-grab active:cursor-grabbing hover:ring-2 ring-primary/40 transition"
                                                        data-testid={`candidate-card-${c.id}`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent text-white grid place-items-center font-semibold text-sm">{c.name[0]}</div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium truncate">{c.name}</div>
                                                                <div className="text-[11px] text-muted-foreground truncate">{c.job_title}</div>
                                                            </div>
                                                        </div>
                                                        {canManage && (
                                                            <div className="flex items-center justify-between mt-2">
                                                                <select
                                                                    value={c.stage}
                                                                    onChange={(e) => moveStage(c.id, e.target.value)}
                                                                    className="text-[11px] bg-transparent border border-input rounded-md px-2 py-1"
                                                                    data-testid={`stage-select-${c.id}`}
                                                                >
                                                                    {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                                                                </select>
                                                                <button
                                                                    onClick={() => setOpenInterview(c)}
                                                                    className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
                                                                    data-testid={`schedule-${c.id}`}
                                                                >
                                                                    <CalIcon className="w-3 h-3" /> Interview
                                                                </button>
                                                            </div>
                                                        )}
                                                    </motion.div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="jobs" className="mt-4">
                    {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : jobs.length === 0 ? (
                        <GlassCard><EmptyState icon={Briefcase} title="No jobs yet" description="Post your first opening to start receiving candidates." /></GlassCard>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {jobs.map((j) => (
                                <GlassCard key={j.id} className="p-5 glass-hover">
                                    <div className="flex justify-between mb-3">
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{j.department}</div>
                                        <StatusBadge status={j.status} />
                                    </div>
                                    <h4 className="font-heading font-semibold text-lg leading-tight">{j.title}</h4>
                                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{j.description}</p>
                                    <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
                                        <span>{j.location}</span>
                                        <span>{j.applicants} applicants</span>
                                    </div>
                                </GlassCard>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {openInterview && <InterviewDialog candidate={openInterview} onClose={() => { setOpenInterview(null); load(); }} />}
        </div>
    );
}

function JobForm({ onCreated }) {
    const [form, setForm] = useState({ title: "", department: "", description: "", location: "Remote", employment_type: "full_time", experience: "", salary_range: "" });
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/jobs", form);
            toast.success("Job posted!");
            onCreated();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    return (
        <form onSubmit={submit} className="space-y-3">
            <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="job-title" /></div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1" /></div>
                <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                    <select value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                        <option value="full_time">Full time</option>
                        <option value="part_time">Part time</option>
                        <option value="internship">Internship</option>
                        <option value="contract">Contract</option>
                    </select>
                </div>
                <div><Label>Experience</Label><Input value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Salary range</Label><Input value={form.salary_range} onChange={(e) => setForm({ ...form, salary_range: e.target.value })} className="mt-1" /></div>
            <div><Label>Description</Label><Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="job-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post job"}
            </Button>
        </form>
    );
}

function InterviewDialog({ candidate, onClose }) {
    const [form, setForm] = useState({ scheduled_at: "", interviewer: "", meet_link: "", notes: "" });
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/interviews", { ...form, candidate_id: candidate.id });
            toast.success("Interview scheduled");
            onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent>
                <DialogHeader><DialogTitle>Schedule interview · {candidate.name}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div><Label>Date & time</Label><Input required type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1" data-testid="interview-when" /></div>
                    <div><Label>Interviewer</Label><Input value={form.interviewer} onChange={(e) => setForm({ ...form, interviewer: e.target.value })} className="mt-1" /></div>
                    <div><Label>Meet link</Label><Input value={form.meet_link} onChange={(e) => setForm({ ...form, meet_link: e.target.value })} placeholder="https://meet.google.com/…" className="mt-1" /></div>
                    <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
                    <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="interview-submit">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schedule"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
